import React, { useState, useRef } from 'react';
import { Button, Form, Alert, Spinner } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { FaArrowLeft } from 'react-icons/fa';
import axios from 'axios';
import { Camera, CameraResultType, CameraSource } from '@capacitor/camera';
import AppNavbar from '../components/AppNavbar';
import BottomNav from '../components/BottomNav';
import ComprobanteTicket from '../components/ComprobanteTicket';
import DepositoClienteModal from '../components/deposito_cliente';
import '../components/AbonoFacturaModal.css';

const dataUrlToFile = (dataUrl: string, filename: string): File => {
  const [header, data] = dataUrl.split(',');
  const mime = header.match(/:(.*?);/)?.[1] ?? 'image/jpeg';
  const binary = atob(data);
  const array = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) array[i] = binary.charCodeAt(i);
  return new File([array], filename, { type: mime });
};

interface Servicio {
  id: number;
  tiposervicio: string;
  direccion: string;
  perfil: string;
  status_user: string;
  costo: string;
}

interface ClienteData {
  id: number;
  nombre: string;
  cedula: string;
  correo: string;
  telefono: string;
  movil: string;
  estado: string;
  servicios: Servicio[];
  facturacion: {
    facturas_nopagadas: number;
    total_facturas: string;
  };
}

interface DepositoResponse {
  fecha: string;
  monto: string;
  numero_comprobante: string;
  banco_emisor: string;
  numero_cuenta_destino: string;
  nombre_destinatario: string;
  validacion_cuenta: string;
  imagen_deposito: string;
}

interface TicketResponse {
  estado: string;
  idticket?: number;
  mensaje: string;
  mensaje_promesa?: string;
  'saldo de cuenta'?: string;
  id_factura_pagada?: number;
}

interface FacturaData {
  id: number;
  estado: string;
  vencimiento?: string;
  emitido?: string;
}

// Tiempo estimado de análisis del comprobante (segundos), solo visual
const TIMEOUT_SEG = 10;

const AbonoFactura: React.FC = () => {
  const navigate = useNavigate();

  const [cedula, setCedula] = useState<string>('');
  const [clienteData, setClienteData] = useState<ClienteData | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [deposito, setDeposito] = useState<File | null>(null);
  const [previewDeposito, setPreviewDeposito] = useState<string>('');
  const [uploadProgress, setUploadProgress] = useState(0);
  const [depositoInfo, setDepositoInfo] = useState<DepositoResponse | null>(null);
  const [depositoValido, setDepositoValido] = useState<boolean | null>(null);
  const [validacionError, setValidacionError] = useState<string | null>(null);
  const [ticketInfo, setTicketInfo] = useState<TicketResponse | null>(null);
  const [factura, setFactura] = useState<FacturaData | null>(null);
  const [loadingFactura, setLoadingFactura] = useState(false);
  const [errorFactura, setErrorFactura] = useState<string | null>(null);
  const [showDetalleFactura, setShowDetalleFactura] = useState(false);
  const [detalleFactura, setDetalleFactura] = useState<any>(null);
  const [pdfFactura, setPdfFactura] = useState<string>('');
  const [loadingDetalleFactura, setLoadingDetalleFactura] = useState(false);
  const [svgComprobante, setSvgComprobante] = useState('');
  const [showComprobante, setShowComprobante] = useState(false);
  const [showDepositoIAModal, setShowDepositoIAModal] = useState(false);
  const [loadingComprobante, setLoadingComprobante] = useState(false);
  const [errorComprobante, setErrorComprobante] = useState('');
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [camError, setCamError] = useState<string | null>(null);
  const [countdown, setCountdown] = useState<number | null>(null);
  const countdownRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const iniciarCuenta = () => {
    let remaining = TIMEOUT_SEG;
    setCountdown(remaining);
    countdownRef.current = setInterval(() => {
      remaining -= 1;
      setCountdown(remaining > 0 ? remaining : null);
      if (remaining <= 0 && countdownRef.current) clearInterval(countdownRef.current);
    }, 1000);
  };

  const detenerCuenta = () => {
    if (countdownRef.current) clearInterval(countdownRef.current);
    setCountdown(null);
  };

  const CuentaRegresiva = ({ valor, label }: { valor: number; label: string }) => (
    <div className="text-center my-3">
      <p className="text-muted mb-1" style={{ fontSize: '0.85rem' }}>{label}</p>
      <div style={{
        width: 64, height: 64, borderRadius: '50%', margin: '0 auto 8px',
        background: `conic-gradient(var(--sn-accion) ${(valor / TIMEOUT_SEG) * 360}deg, var(--sn-superficie-3) 0deg)`,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}>
        <div style={{
          width: 50, height: 50, borderRadius: '50%', background: 'var(--sn-superficie)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontSize: 18, fontWeight: 700, color: 'var(--sn-marca-texto)',
        }}>
          {valor}
        </div>
      </div>
      <div style={{ height: 6, borderRadius: 4, background: 'var(--sn-superficie-3)', overflow: 'hidden', maxWidth: 200, margin: '0 auto' }}>
        <div style={{
          height: '100%', borderRadius: 4, background: 'var(--sn-accion)',
          width: `${(valor / TIMEOUT_SEG) * 100}%`,
          transition: 'width 0.9s linear',
        }} />
      </div>
    </div>
  );

  // Consulta la factura pendiente real del cliente (por idcliente), igual que en Promesa.tsx
  const fetchFacturaPendiente = async (idcliente: number) => {
    setLoadingFactura(true);
    setErrorFactura(null);
    setFactura(null);
    try {
      const res = await axios.post(
        process.env.REACT_APP_N8N_URL_FACTURAS_CLIENTE!,
        { idcliente, limit: 1, estado: '1' },
        { headers: { 'Content-Type': 'application/json' }, timeout: 30000 }
      );
      const facturas: FacturaData[] = res.data?.facturas || [];
      if (facturas.length) {
        setFactura(facturas[0]);
      } else {
        setErrorFactura('Este cliente no tiene facturas pendientes en este momento.');
      }
    } catch {
      setErrorFactura('No se pudo consultar la factura pendiente.');
    } finally {
      setLoadingFactura(false);
    }
  };

  // Ver/ocultar el detalle de la factura pendiente (items, subtotal, IVA)
  const toggleDetalleFactura = async () => {
    if (showDetalleFactura) { setShowDetalleFactura(false); return; }
    setShowDetalleFactura(true);
    if (detalleFactura || !factura) return;

    setLoadingDetalleFactura(true);
    try {
      const [resDetalle, resPdf] = await Promise.allSettled([
        axios.post(
          process.env.REACT_APP_N8N_URL_DETALLE_FACTURA!,
          { facturas_id: String(factura.id) },
          { headers: { 'Content-Type': 'application/json' }, timeout: 20000 }
        ),
        axios.post(
          process.env.REACT_APP_N8N_URL_FACTURA_DETALLE!,
          { idfactura: factura.id },
          { headers: { 'Content-Type': 'application/json' }, timeout: 20000 }
        ),
      ]);

      if (resDetalle.status === 'fulfilled') {
        const raw = Array.isArray(resDetalle.value.data) ? resDetalle.value.data[0] : resDetalle.value.data;
        setDetalleFactura(raw?.estado === 'exito' ? raw : null);
      } else {
        setDetalleFactura(null);
      }

      if (resPdf.status === 'fulfilled') {
        const raw = Array.isArray(resPdf.value.data) ? resPdf.value.data[0] : resPdf.value.data;
        setPdfFactura(raw?.factura?.urlpdf || '');
      }
    } catch {
      setDetalleFactura(null);
    } finally {
      setLoadingDetalleFactura(false);
    }
  };

  // Vuelve a consultar al cliente (misma cédula) para traer la deuda ya actualizada tras el pago
  const refrescarCliente = async () => {
    if (!clienteData) return;
    try {
      const response = await axios.post(
        process.env.REACT_APP_N8N_URL_CLIENTE_CEDULA!,
        { cedula: clienteData.cedula },
        { headers: { 'Content-Type': 'application/json' }, timeout: 30000 }
      );
      if (response.data.estado === 'exito' && response.data.datos?.length > 0) {
        const cli = response.data.datos[0];
        setClienteData(cli);
        fetchFacturaPendiente(cli.id);
      }
    } catch {
      // si falla, se queda con los datos anteriores
    }
  };

  const handleConsultar = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!cedula.trim()) {
      setError('Por favor ingresa un número de cédula');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const response = await axios.post(
        process.env.REACT_APP_N8N_URL_CLIENTE_CEDULA!,
        { cedula },
        { headers: { 'Content-Type': 'application/json' }, timeout: 30000 }
      );

      if (response.data.estado === 'exito' && response.data.datos && response.data.datos.length > 0) {
        const cli = response.data.datos[0];
        setClienteData(cli);
        setError(null);
        fetchFacturaPendiente(cli.id);
      } else {
        setError('No se encontró cliente con esa cédula');
        setClienteData(null);
      }
    } catch (err) {
      let errorMessage = 'Error al buscar cliente';

      if (axios.isAxiosError(err)) {
        if (err.response?.status === 404) {
          errorMessage = 'Cliente no encontrado';
        } else if (err.response?.data?.message) {
          errorMessage = err.response.data.message;
        } else if (err.code === 'ECONNABORTED') {
          errorMessage = 'La solicitud tardó demasiado';
        }
      } else if (err instanceof Error) {
        errorMessage = err.message;
      }

      setError(errorMessage);
      setClienteData(null);
    } finally {
      setLoading(false);
    }
  };

  const handleTakePhoto = async () => {
    setCamError(null);
    try {
      const photo = await Camera.getPhoto({ quality: 80, resultType: CameraResultType.DataUrl, source: CameraSource.Camera, correctOrientation: true });
      if (photo.dataUrl) {
        const f = dataUrlToFile(photo.dataUrl, 'foto_abono.jpg');
        setDeposito(f);
        setPreviewDeposito(photo.dataUrl);
      }
    } catch { setCamError('No se pudo acceder a la cámara.'); }
  };

  const handleGallery = async () => {
    setCamError(null);
    try {
      const photo = await Camera.getPhoto({ quality: 80, resultType: CameraResultType.DataUrl, source: CameraSource.Photos, correctOrientation: true });
      if (photo.dataUrl) {
        const f = dataUrlToFile(photo.dataUrl, 'abono_galeria.jpg');
        setDeposito(f);
        setPreviewDeposito(photo.dataUrl);
      }
    } catch { setCamError('No se pudo abrir la galería.'); }
  };

  const handleSubirDeposito = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!deposito || !clienteData) {
      setError('Por favor selecciona una imagen de depósito');
      return;
    }

    setLoading(true);
    setError(null);
    setUploadProgress(0);

    try {
      const formData = new FormData();
      formData.append('file', deposito);
      formData.append('cliente_id', clienteData.id.toString());
      formData.append('cliente_nombre', clienteData.nombre);
      formData.append('cliente_cedula', clienteData.cedula);
      formData.append('cliente_telefono', clienteData.movil);
      formData.append('monto_adeudado', clienteData.facturacion.total_facturas);
      formData.append('fecha_deposito', new Date().toISOString());

      const response = await axios.post<DepositoResponse>(
        process.env.REACT_APP_N8N_URL_DEPOSITO || 'https://n8n.sisnetel.com/webhook/sisnetel/gpt/comprobantes',
        formData,
        {
          headers: { 'Content-Type': 'multipart/form-data' },
          timeout: 60000,
          onUploadProgress: (progressEvent) => {
            const progress = progressEvent.total
              ? Math.round((progressEvent.loaded / progressEvent.total) * 100)
              : 0;
            setUploadProgress(progress);
            if (progress >= 100) iniciarCuenta(); // imagen ya subida, ahora la IA la está analizando
          },
        }
      );

      setDepositoInfo(response.data);

      if (response.data.validacion_cuenta !== 'validado') {
        setDepositoValido(false);
        setValidacionError('❌ La cuenta del comprobante no es válida. Por favor verifica que sea el depósito correcto.');
        setError(null);
        setUploadProgress(0);
        return;
      }

      const montoDeposito = parseFloat(response.data.monto);
      const montoAdeudado = parseFloat(clienteData.facturacion.total_facturas);
      const montoMinimo = montoAdeudado / 2;

      if (montoDeposito >= montoMinimo) {
        setDepositoValido(true);
        setValidacionError(null);
        setError(null);
      } else {
        setDepositoValido(false);
        setValidacionError(`❌ El monto del depósito ($${montoDeposito.toFixed(2)}) es menor al mínimo requerido ($${montoMinimo.toFixed(2)}).`);
        setError(null);
      }

      setUploadProgress(0);
    } catch (err) {
      let errorMessage = 'Error al enviar el depósito';

      if (axios.isAxiosError(err)) {
        if (err.response?.status === 413) {
          errorMessage = 'El archivo es demasiado grande';
        } else if (err.response?.data?.message) {
          errorMessage = err.response.data.message;
        } else if (err.code === 'ECONNABORTED') {
          errorMessage = 'La solicitud tardó demasiado. Intenta de nuevo.';
        } else if (err.message === 'Network Error') {
          errorMessage = 'Error de conexión. Verifica tu internet.';
        }
      } else if (err instanceof Error) {
        errorMessage = err.message;
      }

      setError(errorMessage);
      setDepositoValido(false);
      setValidacionError(null);
    } finally {
      setLoading(false);
      setUploadProgress(0);
      detenerCuenta();
    }
  };

  // Genera el comprobante visual (SVG) de la factura ya pagada, igual que en el flujo de Pago
  const generarComprobante = async (idFacturaPagada: number) => {
    if (!clienteData) return;
    setLoadingComprobante(true);
    setErrorComprobante('');
    try {
      const resDetalle = await axios.post(
        process.env.REACT_APP_N8N_URL_DETALLE_FACTURA!,
        { facturas_id: String(idFacturaPagada) },
        { headers: { 'Content-Type': 'application/json' }, timeout: 20000 }
      );
      const raw = Array.isArray(resDetalle.data) ? resDetalle.data[0] : resDetalle.data;
      const fac = raw?.factura ?? {};
      const items: any[] = raw?.items ?? [];
      const item = items.find((i: any) => parseFloat(i.total) > 0) || items[0];
      const lines: string[] = (item?.descrp || '').split('\n').map((l: string) => l.trim()).filter(Boolean);

      const now = new Date();
      const p2 = (n: number) => String(n).padStart(2, '0');
      const fechaImpresion = `${p2(now.getDate())}/${p2(now.getMonth() + 1)}/${now.getFullYear()} ${p2(now.getHours())}:${p2(now.getMinutes())}:${p2(now.getSeconds())}`;
      const fmtDate = (d: string) => { if (!d || d.startsWith('0000')) return ''; const pts = d.split('-'); return pts.length === 3 ? `${pts[2]}/${pts[1]}/${pts[0]}` : d; };

      const payload = {
        clienteNombre: clienteData.nombre,
        clienteCedula: clienteData.cedula,
        clienteCelular: clienteData.movil || '',
        clienteCorreo: clienteData.correo || '',
        facturaId: String(fac.id || idFacturaPagada).padStart(8, '0'),
        planNombre: lines[0] || '',
        facturacion: (lines[2] || '').replace('facturación del ', ''),
        suspension: (lines[3] || '').replace('Fecha de Suspensión : ', ''),
        fechaPago: fmtDate(fac.fechapago) || fechaImpresion.split(' ')[0],
        vencimiento: fmtDate(fac.vencimiento),
        subtotal: parseFloat(fac.subtotal || '0').toFixed(2),
        iva: parseFloat(fac.impuesto || '0').toFixed(2),
        total: parseFloat(fac.total || '0').toFixed(2),
        saldo: '0.00',
        pasarela: 'Depósito',
        numeroComprobante: depositoInfo?.numero_comprobante || '',
        autorizacion: '',
        montoDepositado: parseFloat(fac.total || '0').toFixed(2),
        fechaImpresion,
      };

      const res = await axios.post(
        process.env.REACT_APP_N8N_URL_COMPROBANTE!,
        payload,
        { headers: { 'Content-Type': 'application/json' }, timeout: 20000 }
      );
      if (res.data?.base64) {
        setSvgComprobante(res.data.base64);
        setShowComprobante(true);
      } else {
        setErrorComprobante('No se pudo generar el comprobante.');
      }
    } catch {
      setErrorComprobante('Error al generar el comprobante.');
    } finally {
      setLoadingComprobante(false);
    }
  };

  const handleConfirmarPago = async () => {
    if (!deposito || !clienteData || !depositoInfo) {
      setError('Faltan datos para confirmar el pago');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const payload = {
        cliente_id: clienteData.id,
        cliente_nombre: clienteData.nombre,
        cliente_cedula: clienteData.cedula,
        monto_adeudado: clienteData.facturacion.total_facturas,
        monto_abonado: depositoInfo.monto,
        monto_depositado: depositoInfo.monto,
        numero_comprobante: depositoInfo.numero_comprobante,
        banco_emisor: depositoInfo.banco_emisor,
        fecha_deposito: depositoInfo.fecha,
        validacion_cuenta: depositoInfo.validacion_cuenta,
        imagen_deposito: depositoInfo.imagen_deposito,
      };

      const response = await axios.post<TicketResponse | TicketResponse[]>(
        process.env.REACT_APP_N8N_URL_PAGOS_PARTES ||
        'https://n8n.sisnetel.com/webhook/sisnetel/api/v2/pagopartes',
        payload,
        {
          headers: { 'Content-Type': 'application/json' },
          timeout: 30000,
        }
      );

      const data = Array.isArray(response.data) ? response.data[0] : response.data;

      if (data?.estado === 'exito') {
        setTicketInfo(data);
        setSuccess(true);
        setError(null);

        const saldoCero = data['saldo de cuenta'] === '0' && data.id_factura_pagada;
        if (saldoCero) {
          generarComprobante(data.id_factura_pagada!); // deuda saldada: genera el comprobante visual
        }
        refrescarCliente(); // trae la deuda ya actualizada tras el abono
        // No se cierra sola: el admin decide cuándo terminar con el botón "Nueva consulta"
      } else if (data?.estado === 'error') {
        const mensajeError = data.mensaje || 'Error al procesar el pago';
        setError(`❌ ${mensajeError}`);
        setSuccess(false);
        setTicketInfo(null);
      } else {
        setError('❌ Error al procesar el pago. Respuesta inesperada del servidor.');
        setSuccess(false);
        setTicketInfo(null);
      }
    } catch (err) {
      let errorMessage = 'Error al confirmar el pago';

      if (axios.isAxiosError(err)) {
        if (err.response?.status === 400) {
          errorMessage = `❌ ${err.response.data?.message || 'Solicitud inválida'}`;
        } else if (err.response?.status === 404) {
          errorMessage = '❌ El servicio no está disponible';
        } else if (err.response?.status === 500) {
          errorMessage = '❌ Error en el servidor. Intenta de nuevo más tarde.';
        } else if (err.response?.data?.message) {
          errorMessage = `❌ ${err.response.data.message}`;
        } else if (err.code === 'ECONNABORTED') {
          errorMessage = '❌ La solicitud tardó demasiado. Intenta de nuevo.';
        } else if (err.message === 'Network Error') {
          errorMessage = '❌ Error de conexión. Verifica tu internet.';
        }
      } else if (err instanceof Error) {
        errorMessage = `❌ ${err.message}`;
      }

      setError(errorMessage);
      setSuccess(false);
      setTicketInfo(null);
    } finally {
      setLoading(false);
    }
  };

  const handleConsultarDeNuevo = () => {
    setCedula('');
    setClienteData(null);
    setError(null);
    setDeposito(null);
    setPreviewDeposito('');
    setSuccess(false);
    setDepositoInfo(null);
    setDepositoValido(null);
    setValidacionError(null);
    setTicketInfo(null);
    setFactura(null);
    setErrorFactura(null);
    setShowDetalleFactura(false);
    setDetalleFactura(null);
    setPdfFactura('');
    setShowComprobante(false);
    setSvgComprobante('');
    setErrorComprobante('');
  };

  const handleLimpiar = () => {
    setCedula('');
    setClienteData(null);
    setError(null);
    setDeposito(null);
    setPreviewDeposito('');
    setSuccess(false);
    setDepositoInfo(null);
    setDepositoValido(null);
    setValidacionError(null);
    setTicketInfo(null);
    setFactura(null);
    setErrorFactura(null);
    setShowDetalleFactura(false);
    setDetalleFactura(null);
    setPdfFactura('');
    setShowComprobante(false);
    setSvgComprobante('');
    setErrorComprobante('');
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const montoMinimo = clienteData ? parseFloat(clienteData.facturacion.total_facturas) / 2 : 0;
  const esPagoTotal = !!(clienteData && depositoInfo &&
    parseFloat(depositoInfo.monto) >= parseFloat(clienteData.facturacion.total_facturas));

  // Paso actual del asistente (1 a 4)
  const pasoActual = !clienteData ? 1 : !deposito ? 2 : !depositoInfo ? 3 : 4;
  const pasos = [
    { n: 1, label: 'Cédula' },
    { n: 2, label: 'Comprobante' },
    { n: 3, label: 'Validar' },
    { n: 4, label: 'Confirmar' },
  ];

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100vh', overflow: 'hidden' }}>
      <AppNavbar />

      <div style={{ flex: 1, overflowY: 'auto' as const }}>
        <div className="abono-body" style={{ maxWidth: 650, margin: '0 auto', maxHeight: 'none' }}>
          <Button variant="link" onClick={() => navigate(-1)} className="p-0 mb-3">
            <FaArrowLeft /> Volver
          </Button>

          <h4 className="mb-3" style={{ fontWeight: 700, color: 'var(--sn-texto)' }}>💰 Abono a Factura</h4>

          {/* Widget de pasos */}
          <div style={{ display: 'flex', alignItems: 'center', marginBottom: 20 }}>
            {pasos.map((p, i) => (
              <React.Fragment key={p.n}>
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4 }}>
                  <div
                    style={{
                      width: 30, height: 30, borderRadius: '50%',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      fontWeight: 700, fontSize: 13,
                      background: p.n < pasoActual ? 'var(--sn-exito)' : p.n === pasoActual ? 'var(--sn-accion)' : 'var(--sn-superficie-3)',
                      color: p.n < pasoActual ? 'var(--sn-texto-invertido)' : p.n === pasoActual ? 'var(--sn-accion-texto)' : 'var(--sn-texto-3)',
                      transition: 'background 0.2s ease',
                    }}
                  >
                    {p.n < pasoActual ? '✓' : p.n}
                  </div>
                  <span style={{ fontSize: 10, fontWeight: 600, color: p.n === pasoActual ? 'var(--sn-marca)' : 'var(--sn-texto-3)', whiteSpace: 'nowrap' }}>
                    {p.label}
                  </span>
                </div>
                {i < pasos.length - 1 && (
                  <div style={{ flex: 1, height: 3, margin: '0 4px 16px', background: p.n < pasoActual ? 'var(--sn-exito)' : 'var(--sn-borde)', borderRadius: 2, transition: 'background 0.2s ease' }} />
                )}
              </React.Fragment>
            ))}
          </div>

          {error && (
            <Alert variant="danger" className="abono-alert" onClose={() => setError(null)} dismissible>
              {error}
            </Alert>
          )}

          {success && ticketInfo && (
            <>
              {ticketInfo['saldo de cuenta'] === '0' ? (
                <Alert variant="success" className="abono-alert abono-alert-success">
                  <div style={{ textAlign: 'left' }}>
                    <h5 style={{ marginBottom: '8px', fontWeight: 'bold' }}>🎉 ¡Deuda saldada por completo!</h5>
                    <p style={{ marginBottom: '4px' }}><strong>📋 Estado:</strong> {ticketInfo.mensaje}</p>
                    {ticketInfo.id_factura_pagada && (
                      <p style={{ marginBottom: '4px' }}><strong>🧾 Factura pagada:</strong> #{ticketInfo.id_factura_pagada}</p>
                    )}
                    <p style={{ marginBottom: '0px' }}><strong>💰 Saldo actual:</strong> $0.00 — el servicio queda activo, sin promesa de pago pendiente.</p>
                  </div>
                </Alert>
              ) : (
                <Alert variant="success" className="abono-alert abono-alert-success">
                  <div style={{ textAlign: 'left' }}>
                    <h5 style={{ marginBottom: '12px', fontWeight: 'bold' }}>✅ ¡Pago Confirmado!</h5>
                    {ticketInfo.idticket != null && (
                      <p style={{ marginBottom: '8px' }}>
                        <strong>🎫 Ticket ID:</strong> <span style={{ fontSize: '1.1em', color: 'var(--sn-exito-texto)' }}>{ticketInfo.idticket}</span>
                      </p>
                    )}
                    <p style={{ marginBottom: '8px' }}>
                      <strong>📋 Estado:</strong> {ticketInfo.mensaje}
                    </p>
                    {ticketInfo.mensaje_promesa && (
                      <p style={{ marginBottom: '0px' }}>
                        <strong>📅 Promesa de Pago:</strong> {ticketInfo.mensaje_promesa}
                      </p>
                    )}
                  </div>
                </Alert>
              )}

              {!(ticketInfo['saldo de cuenta'] === '0') && (
                <Button variant="secondary" className="w-100 mb-3" onClick={handleLimpiar}>
                  🔁 Nueva consulta
                </Button>
              )}

              {loadingComprobante && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
                  <Spinner animation="border" size="sm" />
                  <span style={{ fontSize: 12, color: 'var(--sn-texto-3)' }}>Generando comprobante…</span>
                </div>
              )}

              {errorComprobante && (
                <Alert variant="warning" className="py-2 mb-3">{errorComprobante}</Alert>
              )}

              {showComprobante && svgComprobante && (
                <div style={{ marginBottom: 12 }}>
                  <ComprobanteTicket
                    svgBase64={svgComprobante}
                    facturaId={ticketInfo.id_factura_pagada || ''}
                    onClose={() => setShowComprobante(false)}
                  />
                  <Button variant="secondary" className="w-100 mt-2" onClick={handleLimpiar}>
                    🔁 Nueva consulta
                  </Button>
                </div>
              )}
            </>
          )}

          {success && !ticketInfo && (
            <Alert variant="success" className="abono-alert abono-alert-success">
              ✅ Pago confirmado exitosamente. Se procesará en breve.
            </Alert>
          )}

          {!clienteData ? (
            <Form onSubmit={handleConsultar} className="abono-form">
              <Form.Group className="mb-4">
                <Form.Label className="abono-form-label">Cédula del Cliente</Form.Label>
                <Form.Control
                  type="text"
                  placeholder="1312908286"
                  value={cedula}
                  onChange={e => {
                    setCedula(e.target.value);
                    setError(null);
                  }}
                  disabled={loading}
                  className="abono-input"
                />
              </Form.Group>

              <div className="abono-button-group">
                <Button type="submit" variant="primary" disabled={loading} className="abono-btn-action" style={{ flex: 'none', paddingLeft: 24, paddingRight: 24 }}>
                  {loading ? <Spinner animation="border" size="sm" /> : '🔍 Consultar'}
                </Button>
              </div>
            </Form>
          ) : !deposito ? (
            <div className="abono-client-card">
              {/* Tarjeta de cliente compacta */}
              <div style={{ background: 'var(--sn-superficie)', borderRadius: 14, border: '1px solid var(--sn-borde)', padding: '10px 14px' }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 }}>
                  <span style={{ fontWeight: 700, fontSize: 13, color: 'var(--sn-marca-fuerte)' }}>👤 {clienteData.nombre}</span>
                  <span style={{
                    fontSize: 10, fontWeight: 700, borderRadius: 999, padding: '2px 8px',
                    background: clienteData.estado === 'ACTIVO' ? 'var(--sn-exito-suave)' : 'var(--sn-peligro-suave)',
                    color: clienteData.estado === 'ACTIVO' ? 'var(--sn-exito-texto)' : 'var(--sn-peligro-texto)',
                  }}>
                    {clienteData.estado}
                  </span>
                </div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '2px 14px', marginBottom: 8 }}>
                  <span style={{ fontSize: 11, color: 'var(--sn-texto-3)' }}>🪪 <span style={{ color: 'var(--sn-texto)', fontWeight: 500 }}>{clienteData.cedula}</span></span>
                  <span style={{ fontSize: 11, color: 'var(--sn-texto-3)' }}>📱 <span style={{ color: 'var(--sn-texto)', fontWeight: 500 }}>{clienteData.movil}</span></span>
                  <span style={{ fontSize: 11, color: 'var(--sn-texto-3)' }}>🧾 <span style={{ color: 'var(--sn-texto)', fontWeight: 500 }}>{clienteData.facturacion.facturas_nopagadas} factura(s)</span></span>
                </div>

                {clienteData.servicios && clienteData.servicios.length > 0 && (
                  <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '2px 10px', paddingTop: 6, paddingBottom: 8, borderTop: '1px solid var(--sn-borde)' }}>
                    <span style={{
                      fontSize: 9, fontWeight: 700, borderRadius: 999, padding: '1px 7px',
                      background: clienteData.servicios[0].status_user?.toLowerCase() === 'online' ? 'var(--sn-exito-suave)' : 'var(--sn-peligro-suave)',
                      color: clienteData.servicios[0].status_user?.toLowerCase() === 'online' ? 'var(--sn-exito-texto)' : 'var(--sn-peligro-texto)',
                    }}>
                      {clienteData.servicios[0].status_user}
                    </span>
                    <span style={{ fontSize: 10, color: 'var(--sn-texto-3)' }}>📶 <span style={{ color: 'var(--sn-texto)', fontWeight: 500 }}>{clienteData.servicios[0].perfil}</span></span>
                    <span style={{ fontSize: 10, color: 'var(--sn-texto-3)' }}>💲 <span style={{ color: 'var(--sn-texto)', fontWeight: 500 }}>${clienteData.servicios[0].costo}</span></span>
                    <span style={{ fontSize: 10, color: 'var(--sn-texto-3)' }}>📍 <span style={{ color: 'var(--sn-texto)', fontWeight: 500 }}>{clienteData.servicios[0].direccion}</span></span>
                  </div>
                )}

                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', paddingTop: 8, borderTop: '1px solid var(--sn-borde)' }}>
                  <span style={{ fontSize: 10, fontWeight: 700, color: 'var(--sn-texto-3)', textTransform: 'uppercase' }}>Total adeudado</span>
                  <span style={{ fontSize: 16, fontWeight: 800, color: 'var(--sn-peligro)' }}>${parseFloat(clienteData.facturacion.total_facturas).toFixed(2)}</span>
                </div>
              </div>

              {/* Factura pendiente real (consultada por idcliente) */}
              <div style={{ background: 'var(--sn-superficie)', borderRadius: 14, border: '1px solid var(--sn-borde)', padding: '10px 14px', marginTop: 8 }}>
                <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--sn-marca)', marginBottom: 6 }}>
                  🧾 Factura pendiente
                </div>
                {loadingFactura ? (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <Spinner animation="border" size="sm" />
                    <span style={{ fontSize: 11, color: 'var(--sn-texto-3)' }}>Consultando factura…</span>
                  </div>
                ) : errorFactura ? (
                  <div style={{ fontSize: 11, color: 'var(--sn-texto-3)' }}>{errorFactura}</div>
                ) : factura ? (
                  <>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <span style={{ fontSize: 11, color: 'var(--sn-texto-3)' }}>
                        ID Factura <span style={{ color: 'var(--sn-texto)', fontWeight: 600 }}>#{factura.id}</span>
                      </span>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                        <span style={{
                          fontSize: 10, fontWeight: 700, borderRadius: 999, padding: '2px 8px',
                          background: factura.estado?.toLowerCase() === 'no pagado' ? 'var(--sn-peligro-suave)' : 'var(--sn-exito-suave)',
                          color: factura.estado?.toLowerCase() === 'no pagado' ? 'var(--sn-peligro-texto)' : 'var(--sn-exito-texto)',
                        }}>
                          {factura.estado}
                        </span>
                        <button
                          onClick={toggleDetalleFactura}
                          style={{
                            fontSize: 10, fontWeight: 700, color: 'var(--sn-info-texto)', background: 'var(--sn-info-suave)',
                            border: '1px solid var(--sn-info)', borderRadius: 999, padding: '3px 9px', cursor: 'pointer',
                          }}
                        >
                          {showDetalleFactura ? 'Ocultar' : '🔍 Ver'}
                        </button>
                      </div>
                    </div>

                    {showDetalleFactura && (
                      <div style={{ marginTop: 8, paddingTop: 8, borderTop: '1px solid var(--sn-borde)' }}>
                        {loadingDetalleFactura ? (
                          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                            <Spinner animation="border" size="sm" />
                            <span style={{ fontSize: 11, color: 'var(--sn-texto-3)' }}>Cargando detalle…</span>
                          </div>
                        ) : detalleFactura?.items?.length ? (
                          <>
                            {detalleFactura.items.map((item: any, k: number) => (
                              <div key={k} style={{ fontSize: 10, color: 'var(--sn-texto-3)', marginBottom: 4, display: 'flex', justifyContent: 'space-between', gap: 8 }}>
                                <span style={{ whiteSpace: 'pre-wrap' }}>{(item.descrp || '').split('\n')[0]}</span>
                                <span style={{ color: 'var(--sn-texto)', fontWeight: 600, flexShrink: 0 }}>${parseFloat(item.total || '0').toFixed(2)}</span>
                              </div>
                            ))}
                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 4, marginTop: 6 }}>
                              <div style={{ background: 'var(--sn-superficie-2)', border: '0.5px solid var(--sn-borde)', borderRadius: 6, padding: '5px 7px' }}>
                                <div style={{ fontSize: 8, color: 'var(--sn-texto-3)', textTransform: 'uppercase' }}>Subtotal</div>
                                <div style={{ fontSize: 10, fontWeight: 600, color: 'var(--sn-texto)' }}>${parseFloat(detalleFactura.factura?.subtotal || '0').toFixed(2)}</div>
                              </div>
                              <div style={{ background: 'var(--sn-superficie-2)', border: '0.5px solid var(--sn-borde)', borderRadius: 6, padding: '5px 7px' }}>
                                <div style={{ fontSize: 8, color: 'var(--sn-texto-3)', textTransform: 'uppercase' }}>IVA</div>
                                <div style={{ fontSize: 10, fontWeight: 600, color: 'var(--sn-texto)' }}>${parseFloat(detalleFactura.factura?.impuesto || '0').toFixed(2)}</div>
                              </div>
                              {detalleFactura.factura?.vencimiento && (
                                <div style={{ gridColumn: '1/-1', background: 'var(--sn-superficie-2)', border: '0.5px solid var(--sn-borde)', borderRadius: 6, padding: '5px 7px' }}>
                                  <div style={{ fontSize: 8, color: 'var(--sn-texto-3)', textTransform: 'uppercase' }}>Vencimiento</div>
                                  <div style={{ fontSize: 10, fontWeight: 600, color: 'var(--sn-texto)' }}>{detalleFactura.factura.vencimiento}</div>
                                </div>
                              )}
                            </div>
                          </>
                        ) : (
                          <div style={{ fontSize: 11, color: 'var(--sn-texto-3)' }}>No se pudo cargar el detalle de la factura.</div>
                        )}

                        {!loadingDetalleFactura && pdfFactura && (
                          <a
                            href={pdfFactura}
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{
                              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
                              marginTop: 8, padding: '8px 12px', borderRadius: 8,
                              background: 'var(--sn-peligro-suave)', color: 'var(--sn-peligro-texto)', fontWeight: 700, fontSize: 11,
                              textDecoration: 'none',
                            }}
                          >
                            📄 Ver PDF de la factura
                          </a>
                        )}
                      </div>
                    )}
                  </>
                ) : (
                  <div style={{ fontSize: 11, color: 'var(--sn-texto-3)' }}>Sin factura pendiente.</div>
                )}
              </div>

              <div className="abono-notice">
                <p className="notice-title">
                  📌 <strong>Solo se acepta mínimo la mitad del valor total adeudado:</strong>
                </p>
                <p className="notice-amount">${montoMinimo.toFixed(2)}</p>
                <p className="notice-subtext">⏰ El abono se activará por 8 días</p>
              </div>

              {camError && <Alert variant="danger" className="py-2 mt-2">{camError}</Alert>}
              <div className="abono-action-buttons">
                <Button variant="outline-secondary" onClick={handleConsultarDeNuevo} className="abono-btn-action">
                  🔄 OTRA CONSULTA
                </Button>
                <button
                  onClick={handleTakePhoto}
                  style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '0.3rem', padding: '0.6rem 0.4rem', background: 'var(--sn-marca-suave)', border: '2px solid var(--sn-marca)', borderRadius: 12, cursor: 'pointer', fontSize: '0.78rem', color: 'var(--sn-marca-texto)', fontWeight: 500 }}
                >
                  <span style={{ fontSize: '1.8rem' }}>📷</span>
                  <span>Tomar foto</span>
                </button>
                <button
                  onClick={handleGallery}
                  style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '0.3rem', padding: '0.6rem 0.4rem', background: 'var(--sn-marca-suave)', border: '2px solid var(--sn-marca)', borderRadius: 12, cursor: 'pointer', fontSize: '0.78rem', color: 'var(--sn-marca-texto)', fontWeight: 500 }}
                >
                  <span style={{ fontSize: '1.8rem' }}>🖼️</span>
                  <span>Galería</span>
                </button>
              </div>
            </div>
          ) : !depositoInfo ? (
            <div className="abono-deposit-confirm">
              <div className="abono-client-info-small">
                <div className="abono-info-row-small">
                  <span className="abono-label-small">CLIENTE:</span>
                  <span className="abono-value-small">{clienteData.nombre}</span>
                </div>
                <div className="abono-info-row-small total-small">
                  <span className="abono-label-small">DEUDA:</span>
                  <span className="abono-total-small">${parseFloat(clienteData.facturacion.total_facturas).toFixed(2)}</span>
                </div>
              </div>

              {previewDeposito && (
                <div className="deposit-preview-container-adaptive">
                  <img src={previewDeposito} alt="Preview Depósito" className="deposit-preview-adaptive" />
                </div>
              )}

              <div className="deposit-file-info">
                <p className="file-name">📎 {deposito?.name}</p>
                <p className="file-size">Tamaño: {((deposito?.size || 0) / 1024).toFixed(2)} KB</p>
              </div>

              {uploadProgress > 0 && uploadProgress < 100 && (
                <div className="deposit-progress">
                  <div className="progress-bar">
                    <div className="progress-fill" style={{ width: `${uploadProgress}%` }}>
                      {uploadProgress}%
                    </div>
                  </div>
                </div>
              )}

              {countdown !== null && (
                <CuentaRegresiva valor={countdown} label="Analizando comprobante con IA..." />
              )}

              <div className="abono-action-buttons">
                <Button
                  variant="outline-secondary"
                  onClick={() => {
                    setDeposito(null);
                    setPreviewDeposito('');
                    if (fileInputRef.current) fileInputRef.current.value = '';
                  }}
                  disabled={loading}
                  className="abono-btn-action"
                >
                  ← ATRÁS
                </Button>
                <Button
                  variant="success"
                  onClick={handleSubirDeposito}
                  disabled={loading}
                  className="abono-btn-upload"
                >
                  {loading ? (
                    <>
                      <Spinner animation="border" size="sm" className="me-2" />
                      Validando...
                    </>
                  ) : (
                    '✓ VALIDAR'
                  )}
                </Button>
              </div>
            </div>
          ) : success ? (
            /* Ya se confirmó el pago: solo mostramos los datos actualizados del cliente */
            <div className="abono-client-card">
              <div style={{ background: 'var(--sn-superficie)', borderRadius: 14, border: '1px solid var(--sn-borde)', padding: '10px 14px' }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 }}>
                  <span style={{ fontWeight: 700, fontSize: 13, color: 'var(--sn-marca-fuerte)' }}>👤 {clienteData.nombre}</span>
                  <span style={{
                    fontSize: 10, fontWeight: 700, borderRadius: 999, padding: '2px 8px',
                    background: clienteData.estado === 'ACTIVO' ? 'var(--sn-exito-suave)' : 'var(--sn-peligro-suave)',
                    color: clienteData.estado === 'ACTIVO' ? 'var(--sn-exito-texto)' : 'var(--sn-peligro-texto)',
                  }}>
                    {clienteData.estado}
                  </span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span style={{ fontSize: 10, fontWeight: 700, color: 'var(--sn-texto-3)', textTransform: 'uppercase' }}>Deuda actualizada</span>
                  <span style={{ fontSize: 16, fontWeight: 800, color: 'var(--sn-peligro)' }}>${parseFloat(clienteData.facturacion.total_facturas).toFixed(2)}</span>
                </div>
              </div>
            </div>
          ) : (
            <div className="abono-validation-result">
              <div className="abono-client-info-small">
                <div className="abono-info-row-small">
                  <span className="abono-label-small">CLIENTE:</span>
                  <span className="abono-value-small">{clienteData.nombre}</span>
                </div>
                <div className="abono-info-row-small total-small">
                  <span className="abono-label-small">DEUDA:</span>
                  <span className="abono-total-small">${parseFloat(clienteData.facturacion.total_facturas).toFixed(2)}</span>
                </div>
              </div>

              <div className="deposit-details">
                <h6 className="deposit-details-title">📋 Detalles del Depósito</h6>
                <div className="deposit-detail-row">
                  <span className="detail-label">Banco:</span>
                  <span className="detail-value">{depositoInfo.banco_emisor}</span>
                </div>
                <div className="deposit-detail-row">
                  <span className="detail-label">Comprobante:</span>
                  <span className="detail-value">{depositoInfo.numero_comprobante}</span>
                </div>
                <div className="deposit-detail-row">
                  <span className="detail-label">Fecha:</span>
                  <span className="detail-value">{depositoInfo.fecha}</span>
                </div>
                <div className="deposit-detail-row">
                  <span className="detail-label">Monto Abonado:</span>
                  <span className="detail-value deposit-amount">${parseFloat(depositoInfo.monto).toFixed(2)}</span>
                </div>
              </div>

              {depositoInfo.validacion_cuenta === 'validado' ? (
                depositoValido === true ? (
                  esPagoTotal ? (
                    <div style={{
                      background: 'var(--sn-alerta-suave)',
                      border: '1px solid var(--sn-alerta)', borderRadius: 8, padding: '10px 12px',
                    }}>
                      <p style={{ margin: 0, fontWeight: 700, fontSize: 13, color: 'var(--sn-alerta-texto)' }}>
                        🏆 ¡Pago completo!
                      </p>
                      <p style={{ margin: '2px 0 0', fontSize: 11, color: 'var(--sn-alerta-texto)' }}>
                        Este depósito cubre el 100% de la deuda total
                      </p>
                    </div>
                  ) : (
                    <div className="validation-success">
                      <p className="validation-text">
                        ✅ <strong>Depósito validado correctamente</strong>
                      </p>
                      <p className="validation-subtext">
                        El monto cubre el requisito mínimo
                      </p>
                    </div>
                  )
                ) : (
                  <div className="validation-error">
                    <p className="validation-text">
                      ❌ <strong>Monto insuficiente</strong>
                    </p>
                    <p className="validation-subtext">
                      {validacionError}
                    </p>
                  </div>
                )
              ) : (
                <div className="validation-error">
                  <p className="validation-text">
                    ❌ <strong>Cuenta no validada</strong>
                  </p>
                  <p className="validation-subtext">
                    La cuenta del comprobante no es válida. Por favor verifica que sea el depósito correcto.
                  </p>
                </div>
              )}

              <div className="abono-action-buttons">
                <Button
                  variant="outline-secondary"
                  onClick={() => {
                    setDeposito(null);
                    setPreviewDeposito('');
                    setDepositoInfo(null);
                    setDepositoValido(null);
                    setValidacionError(null);
                    if (fileInputRef.current) fileInputRef.current.value = '';
                  }}
                  className="abono-btn-action"
                >
                  ← NUEVO DEPÓSITO
                </Button>
                {depositoInfo.validacion_cuenta === 'validado' && depositoValido === true && (
                  esPagoTotal ? (
                    <Button
                      onClick={() => setShowDepositoIAModal(true)}
                      className="abono-btn-upload"
                      style={{ background: 'linear-gradient(135deg, var(--sn-accion), var(--sn-accion-hover))', border: 'none' }}
                    >
                      🏆 PAGAR TOTAL
                    </Button>
                  ) : (
                    <Button
                      variant="success"
                      onClick={handleConfirmarPago}
                      disabled={loading}
                      className="abono-btn-upload"
                    >
                      {loading ? (
                        <>
                          <Spinner animation="border" size="sm" className="me-2" />
                          Procesando...
                        </>
                      ) : (
                        '💳 PAGAR AHORA'
                      )}
                    </Button>
                  )
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      <BottomNav />

      {clienteData && (
        <DepositoClienteModal
          show={showDepositoIAModal}
          onHide={() => setShowDepositoIAModal(false)}
          clienteNombre={clienteData.nombre}
          clienteEstado={clienteData.estado}
          cedula={clienteData.cedula}
        />
      )}
    </div>
  );
};

export default AbonoFactura;
